/**
 * Guard mínimo de roles a nivel de UI.
 *
 * - Si el user no está autenticado → no se debería llegar aquí (`ProtectedRoute`
 *   ya redirigió a /login). Como red de seguridad, redirige igual.
 * - Si está autenticado pero su rol NO está en `allowedRoles`, muestra una
 *   pantalla de "no autorizado" en vez de exponer la UI restringida.
 *
 * Esto NO sustituye a la auth del backend, que sigue siendo la fuente de
 * verdad: las rutas del backend con `require_admin` o auth dual JWT/X-Sync-Token
 * rechazarán igual cualquier petición sin permiso.  El gate de UI solo evita
 * que el usuario vea una pantalla que no puede usar.
 */
import { Navigate } from 'react-router-dom';

import { useAuth } from '@app/auth';
import logoUrl from '@assets/images/italyca-logo.svg';

interface RoleGateProps {
  /** Lista de roles permitidos (case-insensitive). */
  allowedRoles: readonly string[];
  children: React.ReactNode;
  /** Texto del mensaje 403 (default amable, no de error). */
  forbiddenMessage?: string;
}

/**
 * Roles del backend (`middleware/auth.py`).  Se exportan acá como referencia
 * para mantener consistencia entre frontend y backend.
 *
 * `coordinador` queda como alias deprecado mapeado a admin (mientras existan
 * filas con ese rol en BD). Para guards nuevos usar `SUPERADMIN_ROLES` o
 * `ADMIN_ROLES`.
 */
export const SUPERADMIN_ROLES = ['super admin', 'superadmin'] as const;
export const ADMIN_ROLES = [...SUPERADMIN_ROLES, 'admin', 'administrador', 'coordinador'] as const;
export const COORDINATOR_ROLES = ADMIN_ROLES;

function normalize(role: string | null | undefined): string {
  return (role ?? '').trim().toLowerCase();
}

export default function RoleGate({
  allowedRoles,
  children,
  // Mensaje neutro a propósito (no parece error). El gate puede dispararse
  // simplemente porque el usuario navegó a una URL que no le toca.
  forbiddenMessage = 'Esta sección está disponible solo para otros roles. Puedes seguir trabajando desde el menú lateral.',
}: RoleGateProps) {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  const allowed = new Set(allowedRoles.map(normalize));
  if (!allowed.has(normalize(user.role))) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center space-y-5">
        <img
          src={logoUrl}
          alt="Italyca Inmobiliaria"
          className="h-10 w-auto opacity-90"
          width="200"
          height="40"
        />
        <p className="text-brand-gray max-w-md">{forbiddenMessage}</p>
      </div>
    );
  }

  return <>{children}</>;
}
